import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";

const G = "var(--k-green)";
const G_LIGHT = "var(--k-green-soft)";

const SECTIONS = [
  { id: "histoire", title: "1. Notre histoire" },
  { id: "mission", title: "2. Notre mission" },
  { id: "valeurs", title: "3. Nos valeurs" },
  { id: "engagement", title: "4. Notre engagement" },
  { id: "equipe", title: "5. L'équipe" },
];

function SectionBlock({ id, title, children }) {
  return (
    <section id={id} className="mb-12 scroll-mt-24">
      <h2
        className="text-xl font-semibold mb-4 pb-3 border-b"
        style={{ color: "#1a1a1a", borderColor: "#e5e7eb" }}
      >
        {title}
      </h2>
      <div className="text-gray-600 leading-relaxed space-y-3 text-[15px]">
        {children}
      </div>
    </section>
  );
}

function Tag({ children }) {
  return (
    <span
      className="inline-block text-xs font-medium px-2 py-0.5 rounded-full mr-1 mb-1"
      style={{ backgroundColor: G_LIGHT, color: G }}
    >
      {children}
    </span>
  );
}

function InfoBox({ children }) {
  return (
    <div
      className="rounded-xl p-4 text-sm mt-3"
      style={{ backgroundColor: G_LIGHT, borderLeft: `3px solid ${G}` }}
    >
      {children}
    </div>
  );
}

const VALEURS = [
  {
    title: "Souveraineté",
    desc: "Une solution 100 % comorienne, pensée et développée localement.",
  },
  {
    title: "Justesse",
    desc: "Nous prélevons peu, et nous reversons vite. L'essentiel du prix revient à l'hébergeur.",
  },
  {
    title: "Excellence",
    desc: "Formations et accompagnement personnalisé — hygiène, accueil, service — pour porter nos hébergements aux standards internationaux.",
  },
  {
    title: "Ambition régionale",
    desc: "Devenir la référence technologique du tourisme dans l'océan Indien.",
  },
];

export default function About() {
  const [activeSection, setActiveSection] = useState("histoire");
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-20% 0px -70% 0px" }
    );

    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observerRef.current.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div
      className="min-h-screen bg-white"
      style={{ fontFamily: "'DM Sans', sans-serif" }}
    >
      <Helmet>
        <title>À propos — Komorotel</title>
        <meta
          name="description"
          content="Komorotel est la plateforme technologique dédiée à la transformation numérique de l'hébergement touristique aux Comores. Notre histoire, notre mission, nos valeurs."
        />
        <link rel="canonical" href="https://komorotel.com/a-propos" />
        <meta property="og:title" content="À propos — Komorotel" />
        <meta
          property="og:description"
          content="Une plateforme comorienne pour donner une visibilité nationale et internationale à tous les hébergements des Comores."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://komorotel.com/a-propos" />
      </Helmet>

      {/* Header */}
      <div className="border-b border-gray-100 bg-white sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          <a
            href="/"
            className="text-gray-400 hover:text-gray-600 text-sm transition-colors"
          >
            Komorotel
          </a>
          <span className="text-gray-300">/</span>
          <span className="text-sm text-gray-700 font-medium">À propos</span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Page title */}
        <div className="mb-12">
          <p
            className="text-xs font-semibold uppercase tracking-wider mb-3"
            style={{ color: G }}
          >
            Karibu Komorotel
          </p>
          <h1
            className="text-3xl md:text-4xl font-bold mb-3"
            style={{
              fontFamily: "'Playfair Display', serif",
              color: "#1a1a1a",
            }}
          >
            Bienvenue chez vous
          </h1>
          <p className="text-gray-500 mt-3 max-w-2xl leading-relaxed">
            Komorotel est une plateforme technologique dédiée à la
            transformation numérique de l'hébergement touristique aux Comores et
            dans la région.
          </p>
        </div>

        <div className="flex gap-12">
          {/* Sidebar — sticky, hidden on mobile */}
          <aside className="hidden lg:block w-56 shrink-0">
            <div className="sticky top-24">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                Sommaire
              </p>
              <nav className="space-y-1">
                {SECTIONS.map(({ id, title }) => {
                  const isActive = activeSection === id;
                  return (
                    <button
                      key={id}
                      onClick={() => scrollTo(id)}
                      className="block w-full text-left text-sm px-3 py-2 rounded-lg transition-all duration-150"
                      style={{
                        color: isActive ? G : "#6b7280",
                        backgroundColor: isActive ? G_LIGHT : "transparent",
                        fontWeight: isActive ? 600 : 400,
                      }}
                    >
                      {title}
                    </button>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <main className="flex-1 min-w-0">
            <SectionBlock id="histoire" title="1. Notre histoire">
              <p>
                Née de l'initiative d'un professionnel du tourisme, d'un
                ingénieur en technologies et d'un entrepreneur du terrain —
                trois expertises complémentaires, une même connaissance de
                l'écosystème comorien — Komorotel a été conçue pour moderniser
                et structurer le secteur avec passion et ambition.
              </p>
              <div className="mt-2">
                <Tag>Tourisme</Tag>
                <Tag>Technologie</Tag>
                <Tag>Entrepreneuriat</Tag>
              </div>
            </SectionBlock>

            <SectionBlock id="mission" title="2. Notre mission">
              <p>
                Donner une visibilité nationale et internationale à tous les
                hébergements comoriens :
              </p>
              <div className="mt-2">
                <Tag>Hôtels</Tag>
                <Tag>Maisons d'hôtes</Tag>
                <Tag>Auberges</Tag>
                <Tag>Gîtes</Tag>
                <Tag>Studios</Tag>
                <Tag>Appartements</Tag>
              </div>
              <p className="mt-3">
                Grâce à notre logiciel de gestion professionnel, nous aidons les
                propriétaires à mieux gérer leurs réservations, leurs tarifs et
                leurs disponibilités, tout en les accompagnant vers
                l'excellence.
              </p>
              <InfoBox>
                Komorotel n'est pas qu'une vitrine : c'est aussi un outil de
                gestion complet — calendrier, tarifs, disponibilités,
                réservations — pensé pour le quotidien des hébergeurs.
              </InfoBox>
            </SectionBlock>

            <SectionBlock id="valeurs" title="3. Nos valeurs">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
                {VALEURS.map((v, i) => (
                  <div key={i} className="rounded-xl bg-gray-50 p-4">
                    <p className="font-medium text-gray-800 text-sm">
                      {v.title}
                    </p>
                    <p className="text-gray-500 text-xs mt-1 leading-relaxed">
                      {v.desc}
                    </p>
                  </div>
                ))}
              </div>
            </SectionBlock>

            <SectionBlock id="engagement" title="4. Notre engagement">
              <p>
                Komorotel n'est pas une simple plateforme de réservation. C'est
                un partenaire de croissance qui permet aux acteurs du tourisme
                comorien de valoriser leur savoir-faire, d'augmenter leurs
                revenus et de faire rayonner notre hospitalité à travers le
                monde.
              </p>
              <ul className="space-y-2 mt-2">
                {[
                  "Une visibilité nationale et internationale pour chaque hébergement",
                  "Des commissions basses et des reversements rapides",
                  "Un accompagnement à la qualité : hygiène, accueil, service",
                  "Un support disponible, en français et en comorien",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span
                      className="mt-1 shrink-0 w-4 h-4 rounded-full flex items-center justify-center text-white text-xs"
                      style={{ backgroundColor: G }}
                    >
                      ✓
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </SectionBlock>

            <SectionBlock id="equipe" title="5. L'équipe">
              <p>
                Une équipe comorienne passionnée et engagée pour le
                développement touristique des Comores.
              </p>
              <div className="bg-gray-50 rounded-xl p-4 mt-2">
                <p
                  className="text-lg text-gray-800"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Karibu chez vous !
                </p>
                <p className="text-sm font-medium mt-1" style={{ color: G }}>
                  L'équipe Komorotel
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <a
                  href="/hotels"
                  className="inline-flex items-center justify-center rounded-xl px-6 py-3 text-sm font-medium text-white transition-opacity hover:opacity-90"
                  style={{ backgroundColor: G }}
                >
                  Découvrir les hébergements
                </a>
                <a
                  href="/register-hotel"
                  className="inline-flex items-center justify-center rounded-xl border border-gray-200 px-6 py-3 text-sm font-medium text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-50"
                >
                  Inscrire mon hébergement
                </a>
              </div>
            </SectionBlock>
          </main>
        </div>
      </div>
    </div>
  );
}